import { useCallback, useEffect, useMemo, useState } from "react";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import {
  Badge,
  Box,
  CircularProgress,
  Button,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Switch,
  Popover,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import axios from "../../../api/axios";
import { useCurrentUser } from "../../../api/users";
import {
  NOTIFICATIONS_ENDPOINT,
  NOTIFICATIONS_MARK_READ_ENDPOINT,
  NOTIFICATIONS_UNREAD_COUNT_ENDPOINT,
} from "../../../consts";
import { useAuthContext, useHasPermission } from "../../../contexts/auth";
import { NotificationItem } from "../../../types/messages";
import { CurrentUserResponse } from "../../../types/users";
import { formatDateTime } from "../../../utils";
import CustomModal from "../modal/modal.component";

const NOTIFICATIONS_PAGE_SIZE = 20;

type NotificationPreferenceOption = {
  key: string;
  labelKey: string;
  descriptionKey: string;
};

const NOTIFICATION_PREFERENCE_OPTIONS: NotificationPreferenceOption[] = [
  {
    key: "job_status_changed",
    labelKey: "Job status changes",
    descriptionKey: "Notify me when a job status changes.",
  },
  {
    key: "mail_ingestion_counters_added",
    labelKey: "Mail ingestion alerts",
    descriptionKey: "Notify me when mail ingestion adds counters.",
  },
  {
    key: "part_stock_below_threshold",
    labelKey: "Low stock alerts",
    descriptionKey: "Notify me when a part reaches its notification threshold.",
  },
];

/** Where a notification's metadata points, if anywhere. Each entry maps the id
 * a notification carries to the page that id belongs to - a notification with
 * none of these is informational and stays unclickable. */
const NOTIFICATION_TARGETS: { metadataKey: string; buildPath: (id: number) => string }[] = [
  { metadataKey: "job_id", buildPath: (id) => `/service/jobs/${id}` },
  { metadataKey: "part_id", buildPath: (id) => `/service/stock-status/${id}` },
];

const parseMetadataId = (value: unknown): number | null => {
  if (typeof value === "number") {
    return value;
  }
  if (typeof value === "string" && /^\d+$/.test(value)) {
    return Number(value);
  }
  return null;
};

type NotificationsListResponse = {
  results?: NotificationItem[];
};

type NotificationsUnreadCountResponse = {
  count: number;
};

export const NotificationsBell = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser, setCurrentUser } = useAuthContext();
  const { update: updateCurrentUser } = useCurrentUser();
  const canViewNotifications = useHasPermission("messaging.view_notifications");
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationPreferences, setNotificationPreferences] = useState<Record<string, boolean>>({});

  const open = Boolean(anchorEl);

  useEffect(() => {
    const savedPreferences =
      (currentUser?.notification_settings as Record<string, boolean> | undefined) ?? {};
    setNotificationPreferences(savedPreferences);
  }, [currentUser?.notification_settings]);

  const currentNotificationPreferences = useMemo(
    () =>
      NOTIFICATION_PREFERENCE_OPTIONS.reduce<Record<string, boolean>>((acc, option) => {
        acc[option.key] = notificationPreferences[option.key] ?? true;
        return acc;
      }, {}),
    [notificationPreferences]
  );

  const fetchUnreadCount = useCallback(async () => {
    if (!canViewNotifications) {
      setUnreadCount(0);
      return;
    }

    try {
      const { data } = await axios.get<NotificationsUnreadCountResponse>(
        NOTIFICATIONS_UNREAD_COUNT_ENDPOINT
      );
      setUnreadCount(Number(data?.count) || 0);
    } catch {
      setUnreadCount(0);
    }
  }, [canViewNotifications]);

  const fetchNotifications = useCallback(async () => {
    if (!canViewNotifications) {
      setNotifications([]);
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.get<NotificationsListResponse>(NOTIFICATIONS_ENDPOINT, {
        params: { page_size: NOTIFICATIONS_PAGE_SIZE },
      });
      const nextItems = Array.isArray(data?.results)
        ? data.results
        : Array.isArray(data)
          ? data
          : [];
      setNotifications(nextItems);
    } catch {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [canViewNotifications]);

  const markAllAsRead = useCallback(async () => {
    if (!canViewNotifications) {
      return;
    }

    try {
      await axios.post(NOTIFICATIONS_MARK_READ_ENDPOINT);
      setUnreadCount(0);
      setNotifications((prev) => prev.map((item) => ({ ...item, is_read: true })));
    } catch {
      // Best effort; keep UI responsive even if mark-read fails.
    }
  }, [canViewNotifications]);

  useEffect(() => {
    void fetchUnreadCount();
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (!canViewNotifications) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      void fetchUnreadCount();
      if (open) {
        void fetchNotifications();
      }
    }, 30000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [canViewNotifications, fetchNotifications, fetchUnreadCount, open]);

  const handleOpen = async (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    await Promise.all([fetchNotifications(), markAllAsRead()]);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpenSettings = () => {
    setSettingsOpen(true);
  };

  const handleCloseSettings = () => {
    setSettingsOpen(false);
    setNotificationPreferences(
      (currentUser?.notification_settings as Record<string, boolean> | undefined) ?? {}
    );
  };

  const handleTogglePreference = useCallback((key: string) => {
    setNotificationPreferences((previous) => ({
      ...previous,
      [key]: !(previous[key] ?? true),
    }));
  }, []);

  const handleSaveSettings = useCallback(async () => {
    if (!currentUser || savingSettings) {
      return;
    }

    setSavingSettings(true);
    const result = await updateCurrentUser({
      notification_settings: {
        ...(currentUser.notification_settings ?? {}),
        ...currentNotificationPreferences,
      },
    });

    if (result?.success && result?.data) {
      setCurrentUser(result.data as CurrentUserResponse);
      setSettingsOpen(false);
      setNotificationPreferences(
        (result.data.notification_settings as Record<string, boolean> | undefined) ?? {}
      );
    }

    setSavingSettings(false);
  }, [currentNotificationPreferences, currentUser, savingSettings, setCurrentUser, updateCurrentUser]);

  const getTargetPath = useCallback((notification: NotificationItem) => {
    for (const { metadataKey, buildPath } of NOTIFICATION_TARGETS) {
      const id = parseMetadataId(notification.metadata?.[metadataKey]);
      if (id !== null) {
        return buildPath(id);
      }
    }
    return null;
  }, []);

  const handleNotificationClick = useCallback(
    (notification: NotificationItem) => {
      const targetPath = getTargetPath(notification);
      if (!targetPath) {
        return;
      }

      setAnchorEl(null);
      navigate(targetPath);
    },
    [getTargetPath, navigate]
  );

  const listContent = useMemo(() => {
    if (loading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={22} />
        </Box>
      );
    }

    if (notifications.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2 }}>
          {t("No notifications yet")}
        </Typography>
      );
    }

    return (
      <List disablePadding>
        {notifications.map((notification) => (
          <ListItem key={notification.id} alignItems="flex-start" disablePadding divider>
            <ListItemButton
              onClick={() => handleNotificationClick(notification)}
              disabled={!getTargetPath(notification)}
              sx={{
                alignItems: "flex-start",
                cursor: getTargetPath(notification) ? "pointer" : "default",
              }}
            >
              <ListItemText
                primary={notification.body}
                secondary={formatDateTime(notification.created_at)}
                primaryTypographyProps={{
                  variant: "body2",
                  fontWeight: notification.is_read ? 400 : 600,
                }}
                secondaryTypographyProps={{ variant: "caption" }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    );
  }, [getTargetPath, handleNotificationClick, loading, notifications, t]);

  if (!canViewNotifications) {
    return null;
  }

  const notificationSettingsContent = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box
        sx={{
          p: 2,
          borderRadius: 2,
          background:
            "linear-gradient(135deg, color-mix(in srgb, var(--primaryColor) 12%, transparent), color-mix(in srgb, var(--componentBgColor) 92%, white))",
          border: "1px solid var(--borderColor)",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {t("Choose which notification types you want to receive.")}
        </Typography>
      </Box>

      <Stack spacing={1}>
        {NOTIFICATION_PREFERENCE_OPTIONS.map((option) => (
          <Box
            key={option.key}
            sx={{
              borderRadius: 2,
              border: "1px solid var(--borderColor)",
              backgroundColor: "var(--componentBgColor)",
              px: 1.5,
              py: 1,
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={currentNotificationPreferences[option.key] ?? true}
                  onChange={() => handleTogglePreference(option.key)}
                />
              }
              label={
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {t(option.labelKey)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t(option.descriptionKey)}
                  </Typography>
                </Box>
              }
              sx={{ width: "100%", m: 0, alignItems: "flex-start" }}
            />
          </Box>
        ))}
      </Stack>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center", mr: "auto" }}>
          {t("You can change these preferences anytime.")}
        </Typography>
        <Button variant="text" onClick={handleCloseSettings}>
          {t("Cancel")}
        </Button>
        <Button variant="contained" onClick={handleSaveSettings} disabled={savingSettings}>
          {savingSettings ? t("Saving...") : t("Save")}
        </Button>
      </Box>
    </Box>
  );

  return (
    <>
      <IconButton
        onClick={handleOpen}
        aria-label={t("Notifications")}
        size="small"
        sx={{ p: 0.5, mr: 1.5 }}
      >
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <NotificationsNoneIcon />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Box sx={{ width: 360, maxWidth: "90vw" }}>
          <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid", borderColor: "divider", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
            <Typography variant="subtitle2">{t("Notifications")}</Typography>
            <IconButton size="small" onClick={handleOpenSettings} aria-label={t("Notification settings")}>
              <SettingsOutlinedIcon fontSize="small" />
            </IconButton>
          </Box>
          {listContent}
        </Box>
      </Popover>

      <CustomModal
        opened={settingsOpen}
        setOpened={setSettingsOpen}
        title={t("Notification settings")}
        width={520}
        content={notificationSettingsContent}
      />
    </>
  );
};
